import { gql, useQuery } from '@apollo/client';
import { Heading } from '@apollo/orbit';
import classnames from 'classnames';
import React, { useEffect, useMemo } from 'react';

import { ShimmerLoading } from 'src/components/common/loading/ShimmerLoading';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { sortDownstreamWorkflowResults } from '../../helpers';

import {
  DownstreamTaskCard,
  DownstreamTaskCardFragment,
} from './DownstreamTaskCard';

interface Props {
  workflow: GraphQLTypes.CheckWorkflowItemFragment;
  graphId: string;
  className?: string;
}

export const DownstreamCheckWorkflowList = ({
  workflow,
  graphId,
  className,
}: Props) => {
  const { data, loading, stopPolling } = useQuery<
    GraphQLTypes.DownstreamCheckWorkflowListQuery,
    GraphQLTypes.DownstreamCheckWorkflowListQueryVariables
  >(
    gql`
      query DownstreamCheckWorkflowListQuery($graphId: ID!, $workflowId: ID!) {
        service(id: $graphId) {
          id
          checkWorkflow(id: $workflowId) {
            id
            downstreamTask {
              id
              results {
                ...DownstreamTaskCardFragment
              }
            }
          }
        }
      }
      ${DownstreamTaskCardFragment}
    `,
    {
      variables: {
        graphId,
        workflowId: workflow.id,
      },
      pollInterval: 5000,
    },
  );

  const downstreamTaskResults = workflow.downstreamTask?.results;

  const sortedWorkflows = useMemo(() => {
    const results =
      data?.service?.checkWorkflow?.downstreamTask?.results?.filter(
        (result) => result !== null,
      ) ?? [];
    return sortDownstreamWorkflowResults(results);
  }, [data?.service?.checkWorkflow?.downstreamTask?.results]);

  useEffect(() => {
    if (
      isMinLengthArray(1, sortedWorkflows) &&
      sortedWorkflows.every(
        (sortedWorkflow) =>
          sortedWorkflow.downstreamWorkflow?.status !==
          GraphQLTypes.CheckWorkflowStatus.PENDING,
      )
    ) {
      stopPolling();
    }
  }, [sortedWorkflows, stopPolling]);

  if (!loading && sortedWorkflows.length === 0) return null;

  return (
    <div className={classnames(className, 'flex flex-col items-start')}>
      <Heading as="h3" variant="title" className="mb-1">
        Contract checks {`(${downstreamTaskResults?.length})`}
      </Heading>
      {isMinLengthArray(1, sortedWorkflows)
        ? sortedWorkflows.map(
            ({ downstreamWorkflow, failsUpstreamWorkflow }) => (
              <div className="my-1.5" key={downstreamWorkflow?.id}>
                <DownstreamTaskCard
                  downstreamWorkflow={downstreamWorkflow}
                  failsUpstreamWorkflow={failsUpstreamWorkflow}
                />
              </div>
            ),
          )
        : Array.from({ length: 2 }).map((_, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={`${workflow.id}_${i}`}>
              <ShimmerLoading className="my-2 mt-4 w-32" />
              <ShimmerLoading className="my-2 mb-4 mt-1 w-80" />
            </React.Fragment>
          ))}
    </div>
  );
};
